import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, SearchIcon, FilterIcon, DownloadIcon, XIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
export function TransactionMonitoring() {
  const [timeWindow, setTimeWindow] = useState<'live' | '15m' | '1h'>('live');
  const [selectedBank, setSelectedBank] = useState<string>('all');
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'success' | 'failed'>('all');
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const liveUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const banks = [{
    code: 'all',
    name: 'All Banks'
  }, {
    code: 'AMHRETAA',
    name: 'Amhara Bank S.C.'
  }, {
    code: 'AWINETAA',
    name: 'Awash Bank S.C.'
  }, {
    code: 'CBETETAA',
    name: 'Commercial Bank of Ethiopia (CBE)'
  }, {
    code: 'BUNAETAA',
    name: 'Bunna International Bank S.C.'
  }, {
    code: 'DASHETAA',
    name: 'Dashen Bank S.C.'
  }, {
    code: 'CBORETAA',
    name: 'Cooperative Bank of Oromia S.C. (COOP)'
  }, {
    code: 'ZEMEETAA',
    name: 'Zemen Bank S.C.'
  }, {
    code: 'NIBIETAA',
    name: 'NIB International Bank S.C. (NIB)'
  }, {
    code: 'HIJRETAA',
    name: 'Hibret Bank S.C.'
  }, {
    code: 'TSCPETAA',
    name: 'Tsehay Bank S.C.'
  }, {
    code: 'WEGAETAA',
    name: 'Wegagen Bank S.C.'
  }, {
    code: 'ABAYETAA',
    name: 'Abay Bank S.C.'
  }];
  // Initial sample data - this would typically come from an API
  const initialTransactions = [{
    id: 1,
    dateTime: '2023-05-10 14:30:22',
    senderBankBIC: 'AMHRETAA',
    senderBankName: 'Amhara Bank S.C.',
    senderAccount: '1000123456789',
    receiverBankBIC: 'CBETETAA',
    receiverBankName: 'Commercial Bank of Ethiopia (CBE)',
    receiverAccount: '2000987654321',
    amount: 5000,
    purposeCode: 'P2P',
    status: 'Success',
    failureReason: '',
    narration: 'Payment for services'
  }, {
    id: 2,
    dateTime: '2023-05-10 14:28:15',
    senderBankBIC: 'AWINETAA',
    senderBankName: 'Awash Bank S.C.',
    senderAccount: '1000223456789',
    receiverBankBIC: 'DASHETAA',
    receiverBankName: 'Dashen Bank S.C.',
    receiverAccount: '2000887654321',
    amount: 3500,
    purposeCode: 'BILL',
    status: 'Failed',
    failureReason: 'Insufficient funds',
    narration: 'Utility payment'
  }, {
    id: 3,
    dateTime: '2023-05-10 14:25:47',
    senderBankBIC: 'BUNAETAA',
    senderBankName: 'Bunna International Bank S.C.',
    senderAccount: '1000323456789',
    receiverBankBIC: 'ZEMEETAA',
    receiverBankName: 'Zemen Bank S.C.',
    receiverAccount: '2000787654321',
    amount: 2800,
    purposeCode: 'P2P',
    status: 'Success',
    failureReason: '',
    narration: 'Loan repayment'
  }, {
    id: 4,
    dateTime: '2023-05-10 14:20:33',
    senderBankBIC: 'NIBIETAA',
    senderBankName: 'NIB International Bank S.C. (NIB)',
    senderAccount: '1000423456789',
    receiverBankBIC: 'HIJRETAA',
    receiverBankName: 'Hibret Bank S.C.',
    receiverAccount: '2000687654321',
    amount: 4200,
    purposeCode: 'SALARY',
    status: 'Success',
    failureReason: '',
    narration: 'Monthly salary'
  }, {
    id: 5,
    dateTime: '2023-05-10 14:15:19',
    senderBankBIC: 'TSCPETAA',
    senderBankName: 'Tsehay Bank S.C.',
    senderAccount: '1000523456789',
    receiverBankBIC: 'WEGAETAA',
    receiverBankName: 'Wegagen Bank S.C.',
    receiverAccount: '2000587654321',
    amount: 1500,
    purposeCode: 'P2P',
    status: 'Failed',
    failureReason: 'Account not found',
    narration: 'Refund'
  }];
  // Function to generate a random transaction for real-time updates
  const generateRandomTransaction = (id: number) => {
    const senderBankIndex = Math.floor(Math.random() * (banks.length - 1)) + 1;
    const receiverBankIndex = Math.floor(Math.random() * (banks.length - 1)) + 1;
    const senderBank = banks[senderBankIndex];
    const receiverBank = banks[receiverBankIndex];
    const status = Math.random() > 0.8 ? 'Failed' : 'Success';
    const purposeCodes = ['P2P', 'BILL', 'SALARY', 'TRANSFER', 'PAYMENT'];
    const purposeCode = purposeCodes[Math.floor(Math.random() * purposeCodes.length)];
    const amount = Math.floor(Math.random() * 10000) + 100;
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const dateTime = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${hours}:${minutes}:${seconds}`;
    return {
      id,
      dateTime,
      senderBankBIC: senderBank.code,
      senderBankName: senderBank.name,
      senderAccount: `1000${Math.floor(Math.random() * 1000000000)}`,
      receiverBankBIC: receiverBank.code,
      receiverBankName: receiverBank.name,
      receiverAccount: `2000${Math.floor(Math.random() * 1000000000)}`,
      amount,
      purposeCode,
      status,
      failureReason: status === 'Failed' ? ['Insufficient funds', 'Account not found', 'Transaction timeout', 'Invalid account'][Math.floor(Math.random() * 4)] : '',
      narration: ['Payment for services', 'Utility payment', 'Loan repayment', 'Monthly salary', 'Refund', 'Transfer', 'Bill payment'][Math.floor(Math.random() * 7)]
    };
  };
  // Generate 1000 sample transactions for pagination
  useEffect(() => {
    setLoading(true);
    const generatedTransactions = [...initialTransactions];
    // Generate more transactions to reach 1000
    for (let i = 6; i <= 1000; i++) {
      generatedTransactions.push(generateRandomTransaction(i));
    }
    setTransactions(generatedTransactions);
    setLoading(false);
  }, []);
  // Handle live updates
  useEffect(() => {
    if (timeWindow === 'live') {
      liveUpdateIntervalRef.current = setInterval(() => {
        setTransactions(prev => {
          const newTransaction = generateRandomTransaction(prev.length + 1);
          return [newTransaction, ...prev];
        });
      }, 5000); // Add a new transaction every 5 seconds
    } else {
      if (liveUpdateIntervalRef.current) {
        clearInterval(liveUpdateIntervalRef.current);
      }
    }
    return () => {
      if (liveUpdateIntervalRef.current) {
        clearInterval(liveUpdateIntervalRef.current);
      }
    };
  }, [timeWindow]);
  // Handle outside clicks for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.bank-dropdown')) {
        setShowBankDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const handleRowClick = (transaction: any) => {
    setSelectedTransaction(transaction);
    setShowDetails(true);
  };
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };
  // Apply filters to transactions
  const filteredTransactions = transactions.filter(transaction => {
    // Apply search filter
    const searchLower = searchTerm.toLowerCase();
    if (searchTerm && !transaction.senderBankBIC.toLowerCase().includes(searchLower) && !transaction.senderBankName.toLowerCase().includes(searchLower) && !transaction.receiverBankBIC.toLowerCase().includes(searchLower) && !transaction.receiverBankName.toLowerCase().includes(searchLower) && !transaction.senderAccount.toLowerCase().includes(searchLower) && !transaction.receiverAccount.toLowerCase().includes(searchLower) && !transaction.purposeCode.toLowerCase().includes(searchLower)) {
      return false;
    }
    // Apply status filter
    if (selectedStatus !== 'all' && selectedStatus === 'success' && transaction.status !== 'Success') {
      return false;
    }
    if (selectedStatus !== 'all' && selectedStatus === 'failed' && transaction.status !== 'Failed') {
      return false;
    }
    // Apply bank filter
    if (selectedBank !== 'all' && transaction.senderBankBIC !== selectedBank && transaction.receiverBankBIC !== selectedBank) {
      return false;
    }
    return true;
  });
  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTransactions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };
  return <div className="space-y-6" data-id="element-1209">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1210">
        <h1 className="text-2xl font-bold text-[#1A1F36]" data-id="element-1211">
          Transaction Monitoring
        </h1>
        <div className="flex items-center gap-3" data-id="element-1212">
          <button onClick={() => setTimeWindow('live')} className={`px-3 py-1 text-sm rounded-md ${timeWindow === 'live' ? 'bg-[#F58220] text-white' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1213">
            Live
          </button>
          <button onClick={() => setTimeWindow('15m')} className={`px-3 py-1 text-sm rounded-md ${timeWindow === '15m' ? 'bg-[#F58220] text-white' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1214">
            15m
          </button>
          <button onClick={() => setTimeWindow('1h')} className={`px-3 py-1 text-sm rounded-md ${timeWindow === '1h' ? 'bg-[#F58220] text-white' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1215">
            1h
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-1216">
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1217">
          <div className="flex flex-wrap items-center gap-3" data-id="element-1218">
            <div className="relative bank-dropdown" data-id="element-1219">
              <button onClick={() => setShowBankDropdown(!showBankDropdown)} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1220">
                <FilterIcon size={16} className="mr-2" data-id="element-1221" />
                {selectedBank === 'all' ? 'All Banks' : banks.find(b => b.code === selectedBank)?.name}
                <ChevronDownIcon size={16} className="ml-2" data-id="element-1222" />
              </button>
              {showBankDropdown && <div className="absolute left-0 mt-1 w-64 bg-white rounded-md shadow-lg z-10 border border-gray-200 max-h-60 overflow-y-auto" data-id="element-1223">
                  {banks.map(bank => <button key={bank.code} onClick={() => {
                setSelectedBank(bank.code);
                setShowBankDropdown(false);
              }} className={`block w-full text-left px-4 py-2 text-sm ${selectedBank === bank.code ? 'bg-gray-100 text-[#F58220]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1224">
                      {bank.name}
                    </button>)}
                </div>}
            </div>
            <div className="flex items-center gap-2" data-id="element-1225">
              <button onClick={() => setSelectedStatus('all')} className={`px-3 py-1 text-xs rounded-full ${selectedStatus === 'all' ? 'bg-gray-200 text-gray-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1226">
                All
              </button>
              <button onClick={() => setSelectedStatus('success')} className={`px-3 py-1 text-xs rounded-full ${selectedStatus === 'success' ? 'bg-green-100 text-green-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1227">
                Success
              </button>
              <button onClick={() => setSelectedStatus('failed')} className={`px-3 py-1 text-xs rounded-full ${selectedStatus === 'failed' ? 'bg-red-100 text-red-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1228">
                Failed
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-1229">
            <div className="relative flex-grow" data-id="element-1230">
              <input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearch} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-1231" />
              <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" data-id="element-1232" />
            </div>
            <button className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1233">
              <DownloadIcon size={16} className="mr-2" data-id="element-1234" />
              Export
            </button>
          </div>
        </div>
        {loading ? <div className="p-8 text-center" data-id="element-1235">
            <p className="text-gray-500" data-id="element-1236">Loading transactions...</p>
          </div> : <>
            <div className="overflow-x-auto" data-id="element-1237">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1238">
                <thead className="bg-gray-50 sticky top-0" data-id="element-1239">
                  <tr data-id="element-1240">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1241">
                      Date Time
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1242">
                      Sender Bank BIC
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1243">
                      Sender Bank Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1244">
                      Sender Account
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1245">
                      Receiver Bank BIC
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1246">
                      Receiver Bank Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1247">
                      Receiver Account
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1248">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1249">
                      Purpose Code
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1250">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1251">
                  {currentItems.length > 0 ? currentItems.map(transaction => <tr key={transaction.id} onClick={() => handleRowClick(transaction)} className={`cursor-pointer hover:bg-gray-50 ${transaction.status === 'Failed' ? 'bg-red-50' : ''}`} data-id="element-1252">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1253">
                          {transaction.dateTime}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1254">
                          {transaction.senderBankBIC}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1255">
                          {transaction.senderBankName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1256">
                          {transaction.senderAccount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1257">
                          {transaction.receiverBankBIC}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1258">
                          {transaction.receiverBankName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1259">
                          {transaction.receiverAccount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1260">
                          ETB {transaction.amount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1261">
                          {transaction.purposeCode}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-1262">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${transaction.status === 'Success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1263">
                            {transaction.status}
                          </span>
                        </td>
                      </tr>) : <tr data-id="element-1264">
                      <td colSpan={10} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-1265">
                        No transactions found matching your criteria
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 flex flex-col sm:flex-row justify-between items-center border-t border-gray-200" data-id="element-1266">
              <div className="flex items-center mb-4 sm:mb-0" data-id="element-1267">
                <span className="text-sm text-gray-700 mr-2" data-id="element-1268">
                  Showing {indexOfFirstItem + 1}-
                  {Math.min(indexOfLastItem, filteredTransactions.length)} of{' '}
                  {filteredTransactions.length} transactions
                </span>
                <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="ml-2 border border-gray-300 rounded-md text-sm py-1 px-2" data-id="element-1269">
                  <option value={10} data-id="element-1270">10 per page</option>
                  <option value={25} data-id="element-1271">25 per page</option>
                  <option value={50} data-id="element-1272">50 per page</option>
                  <option value={100} data-id="element-1273">100 per page</option>
                </select>
              </div>
              <div className="flex items-center space-x-2" data-id="element-1274">
                <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className={`px-2 py-1 text-sm rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1275">
                  First
                </button>
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className={`p-1 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1276">
                  <ChevronLeftIcon size={16} data-id="element-1277" />
                </button>
                <div className="flex items-center space-x-1" data-id="element-1278">
                  {Array.from({
                length: Math.min(5, totalPages)
              }, (_, i) => {
                // Show 5 pages around current page
                let pageNum;
                if (totalPages <= 5) {
                  // Less than 5 pages total, show all
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  // Near the start
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  // Near the end
                  pageNum = totalPages - 4 + i;
                } else {
                  // In the middle
                  pageNum = currentPage - 2 + i;
                }
                return <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`w-8 h-8 flex items-center justify-center rounded-md ${currentPage === pageNum ? 'bg-[#F58220] text-white' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1279">
                          {pageNum}
                        </button>;
              })}
                  {totalPages > 5 && currentPage < totalPages - 2 && <>
                      <span className="px-1" data-id="element-1280">...</span>
                      <button onClick={() => handlePageChange(totalPages)} className="w-8 h-8 flex items-center justify-center rounded-md text-gray-700 hover:bg-gray-100" data-id="element-1281">
                        {totalPages}
                      </button>
                    </>}
                </div>
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className={`p-1 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1282">
                  <ChevronRightIcon size={16} data-id="element-1283" />
                </button>
                <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className={`px-2 py-1 text-sm rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1284">
                  Last
                </button>
              </div>
            </div>
          </>}
      </div>
      {showDetails && selectedTransaction && <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-10" data-id="element-1285">
          <div className="bg-white w-full max-w-md h-full overflow-auto" data-id="element-1286">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1287">
              <h3 className="font-medium text-[#1A1F36]" data-id="element-1288">
                Transaction Details
              </h3>
              <button onClick={() => setShowDetails(false)} className="text-gray-500 hover:text-gray-700" data-id="element-1289">
                <XIcon size={20} data-id="element-1290" />
              </button>
            </div>
            <div className="p-4 space-y-4" data-id="element-1291">
              <div data-id="element-1292">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1293">
                  Status
                </h4>
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${selectedTransaction.status === 'Success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1294">
                  {selectedTransaction.status}
                </span>
                {selectedTransaction.failureReason && <p className="mt-1 text-sm text-red-600" data-id="element-1295">
                    {selectedTransaction.failureReason}
                  </p>}
              </div>
              <div data-id="element-1296">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1297">
                  Date & Time
                </h4>
                <p className="text-sm text-gray-900" data-id="element-1298">
                  {selectedTransaction.dateTime}
                </p>
              </div>
              <div data-id="element-1299">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1300">
                  Amount
                </h4>
                <p className="text-lg font-semibold text-[#1A1F36]" data-id="element-1301">
                  ETB {selectedTransaction.amount.toLocaleString()}
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200" data-id="element-1302">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1303">
                  Sender Details
                </h4>
                <div className="space-y-2" data-id="element-1304">
                  <div data-id="element-1305">
                    <p className="text-xs text-gray-500" data-id="element-1306">Bank BIC</p>
                    <p className="text-sm text-gray-900" data-id="element-1307">
                      {selectedTransaction.senderBankBIC}
                    </p>
                  </div>
                  <div data-id="element-1308">
                    <p className="text-xs text-gray-500" data-id="element-1309">Bank Name</p>
                    <p className="text-sm text-gray-900" data-id="element-1310">
                      {selectedTransaction.senderBankName}
                    </p>
                  </div>
                  <div data-id="element-1311">
                    <p className="text-xs text-gray-500" data-id="element-1312">Account</p>
                    <p className="text-sm text-gray-900" data-id="element-1313">
                      {selectedTransaction.senderAccount}
                    </p>
                  </div>
                </div>
              </div>
              <div className="pt-2 border-t border-gray-200" data-id="element-1314">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1315">
                  Receiver Details
                </h4>
                <div className="space-y-2" data-id="element-1316">
                  <div data-id="element-1317">
                    <p className="text-xs text-gray-500" data-id="element-1318">Bank BIC</p>
                    <p className="text-sm text-gray-900" data-id="element-1319">
                      {selectedTransaction.receiverBankBIC}
                    </p>
                  </div>
                  <div data-id="element-1320">
                    <p className="text-xs text-gray-500" data-id="element-1321">Bank Name</p>
                    <p className="text-sm text-gray-900" data-id="element-1322">
                      {selectedTransaction.receiverBankName}
                    </p>
                  </div>
                  <div data-id="element-1323">
                    <p className="text-xs text-gray-500" data-id="element-1324">Account</p>
                    <p className="text-sm text-gray-900" data-id="element-1325">
                      {selectedTransaction.receiverAccount}
                    </p>
                  </div>
                </div>
              </div>
              <div className="pt-2 border-t border-gray-200" data-id="element-1326">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1327">
                  Additional Information
                </h4>
                <div className="space-y-2" data-id="element-1328">
                  <div data-id="element-1329">
                    <p className="text-xs text-gray-500" data-id="element-1330">Purpose Code</p>
                    <p className="text-sm text-gray-900" data-id="element-1331">
                      {selectedTransaction.purposeCode}
                    </p>
                  </div>
                  <div data-id="element-1332">
                    <p className="text-xs text-gray-500" data-id="element-1333">Narration</p>
                    <p className="text-sm text-gray-900" data-id="element-1334">
                      {selectedTransaction.narration}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}